import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { fetchProjectJobs, fetchPlanningLookups, fetchJobTasks, fetchTeamLoad, createProjectAssignment, fetchProjectsList, fetchAssignment, updateAssignment, fetchManagerClients, fetchVacationWarnings, fetchMe } from '../../api';
import DatePicker from '../../ui/DatePicker.jsx';
import { useAppRefresh } from '../../context/AppRefreshContext';

function getTodayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatDM(ymd) {
  const s = String(ymd || '').slice(0, 10);
  if (s.length !== 10) return '—';
  const dd = s.slice(8, 10);
  const mm = s.slice(5, 7);
  if (!dd || !mm) return '—';
  return `${dd}/${mm}`;
}

function weekdayNameFromYmd(ymd) {
  const s = String(ymd || '').slice(0, 10);
  if (s.length !== 10) return '';
  const d = new Date(`${s}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (_) {
    return '';
  }
}

const HIDDEN_ASSIGNEE_NAMES = new Set(['admin', 'manager test', 'manager']);
function isHiddenAssigneeName(name) {
  const n = String(name || '').trim().toLowerCase();
  return HIDDEN_ASSIGNEE_NAMES.has(n);
}

export default function SharedAddWorkModal({
  open = true,
  project,
  assignmentId = null,
  mode = 'create',
  initialAssignment = null,
  rangeKey = 'this_week',
  initialAssigneeIds = [],
  initialStartDate,
  initialDueDate,
  initialJobId,
  initialTaskId,
  initialPlannedHours,
  initialStatus,
  initialNotes,
  initialTopPriority,
  onClose,
  onCreated,
  onSaved,
  overview,
}) {
  const { refreshKey: appRefreshKey, bumpRefresh } = useAppRefresh();
  const isOpen = open !== false;

  const normalizedMode = String(mode || '').toLowerCase();
  const effectiveAssignmentId = assignmentId
    ?? initialAssignment?.assignmentId
    ?? initialAssignment?.id
    ?? null;
  const isEditing = normalizedMode === 'edit' || !!effectiveAssignmentId;
  const fixedProject = !!project?.id || isEditing;
  const [selectedProject, setSelectedProject] = useState(project || null);
  const [projects, setProjects] = useState([]);

  const [me, setMe] = useState(null);
  const [includeMe, setIncludeMe] = useState(false);

  const [clients, setClients] = useState([]); // [{id,name,color}]
  const [selectedClientId, setSelectedClientId] = useState(() => (project?.client_id != null ? String(project.client_id) : ''));

  const [jobs, setJobs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [selectedManagerIds, setSelectedManagerIds] = useState([]); // checkbox UX, but must be max 1
  const [managerBucketError, setManagerBucketError] = useState('');
  const [assignmentCreatedByManagerId, setAssignmentCreatedByManagerId] = useState(null);
  const includeMeTouchedRef = useRef(false);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [form, setForm] = useState(() => {
    const today = getTodayYmd();
    const start = initialStartDate || today;
    const due = initialDueDate || start;
    return {
      job_id: initialJobId != null && initialJobId !== '' ? String(initialJobId) : '',
      task_id: initialTaskId != null && initialTaskId !== '' ? String(initialTaskId) : '',
      planned_hours: initialPlannedHours != null && initialPlannedHours !== '' ? String(initialPlannedHours) : '',
      start_date: start,
      due_date: due,
      notes: initialNotes != null ? String(initialNotes) : '',
      top_priority: !!initialTopPriority,
    };
  });
  const [assignees, setAssignees] = useState([]);
  const [originalAssigneeIds, setOriginalAssigneeIds] = useState([]);
  const [createdNewAssigneeIds, setCreatedNewAssigneeIds] = useState([]);
  const [splitMode, setSplitMode] = useState('per_person'); // per_person | split_total
  const [split, setSplit] = useState(false);
  const [distributionMode, setDistributionMode] = useState('per_day'); // spread | per_day
  const [includeWeekends, setIncludeWeekends] = useState(false);

  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState('');
  const [repeatSingleDayNote, setRepeatSingleDayNote] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingLookups, setLoadingLookups] = useState(false);

  const meId = useMemo(() => (me?.id != null ? String(me.id) : null), [me?.id]);
  const meRole = useMemo(() => String(me?.role || '').trim().toLowerCase(), [me?.role]);
  const isMeManagerLike = useMemo(() => ['manager', 'admin', 'junior_manager'].includes(meRole), [meRole]);

  const selectedManagerId = useMemo(() => {
    const ids = Array.isArray(selectedManagerIds) ? selectedManagerIds : [];
    return ids.length === 1 ? ids[0] : null;
  }, [selectedManagerIds]);

  const managerIdSet = useMemo(
    () => new Set((Array.isArray(managerOptions) ? managerOptions : []).map(m => String(m.id))),
    [managerOptions]
  );

  const managerAssigneeIds = useMemo(() => {
    const out = new Set();
    // Managers selection acts as assignees only when Employees are non-empty.
    if (assignees.length > 0) {
      const ids = Array.isArray(selectedManagerIds) ? selectedManagerIds : [];
      ids.map(String).filter(Boolean).forEach(id => out.add(id));
    }

    // Include-me always adds current user as an assignee.
    if (includeMe && meId) out.add(String(meId));

    return Array.from(out);
  }, [assignees.length, selectedManagerIds, includeMe, meId]);

  const hasAssigneeMode = useMemo(() => assignees.length > 0 || includeMe, [assignees.length, includeMe]);

  // Vacation/Sick warning (manager UX): show ONLY when there is a real conflict.
  const vacationWindow = useMemo(() => {
    const to = String(form.due_date || '').slice(0, 10) || '';
    const from = String(form.start_date || '').slice(0, 10) || to;
    if (!from || !to) return { from: '', to: '' };
    if (String(to) < String(from)) return { from: to, to: from };
    return { from, to };
  }, [form.start_date, form.due_date]);

  const [vacationWarnings, setVacationWarnings] = useState([]);

  const repeatWeekday = useMemo(() => weekdayNameFromYmd(form.start_date || form.due_date) || '', [form.start_date, form.due_date]);

  useEffect(() => {
    if (!isOpen) return;
    // Reset repeat fields whenever this modal is opened for a (potentially) different item.
    setRepeatWeekly(false);
    setRepeatUntil('');
    setRepeatSingleDayNote(false);
  }, [effectiveAssignmentId, isEditing, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEditing) return;
    // Default for new work planning.
    setDistributionMode('per_day');
  }, [isEditing, isOpen]);

  useEffect(() => {
    let cancelled = false;

    async function loadVacationWarnings() {
      if (!isOpen) return;
      if (!vacationWindow.from || !vacationWindow.to) {
        setVacationWarnings([]);
        return;
      }

      const plannedHoursForWarnings = (() => {
        const raw = String(form.planned_hours || '').trim();
        if (!raw) return 0;
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      })();

      const userIds = (Array.isArray(assignees) ? assignees : [])
        .map(a => (a?.user_id != null ? String(a.user_id) : ''))
        .filter(Boolean);

      const allUserIds = hasAssigneeMode
        ? Array.from(new Set([...userIds, ...(Array.isArray(managerAssigneeIds) ? managerAssigneeIds : [])]))
        : userIds;

      if (allUserIds.length === 0) {
        setVacationWarnings([]);
        return;
      }

      if (!(plannedHoursForWarnings > 0)) {
        setVacationWarnings([]);
        return;
      }

      const plannedHoursSplits = (splitMode === 'split_total' && split)
        ? userIds.map(uid => ({
          user_id: uid,
          planned_hours_split: Number((assignees.find(a => String(a.user_id) === String(uid))?.planned_hours_split) || 0),
        }))
        : undefined;

      try {
        const res = await fetchVacationWarnings({
          user_ids: allUserIds,
          planned_hours: plannedHoursForWarnings,
          split_mode: splitMode,
          distribution_mode: distributionMode,
          include_weekends: includeWeekends,
          start_date: form.start_date ? String(form.start_date).slice(0, 10) : null,
          due_date: String(form.due_date || '').slice(0, 10),
          planned_hours_splits: plannedHoursSplits,
          assignment_id: isEditing ? (assignmentId || initialAssignment?.id || null) : null,
          task_id: form.task_id || null,
        });

        const warnings = Array.isArray(res?.warnings) ? res.warnings : [];
        if (!cancelled) setVacationWarnings(warnings);
      } catch (err) {
        // Spec: show nothing unless there is a real conflict.
        // If the warning check fails, keep the UI silent.
        if (!cancelled) setVacationWarnings([]);
      }
    }

    loadVacationWarnings();
    return () => { cancelled = true; };
  }, [
    assignees,
    assignmentId,
    distributionMode,
    form.due_date,
    form.planned_hours,
    form.start_date,
    form.task_id,
    includeWeekends,
    initialAssignment?.id,
    isEditing,
    isOpen,
    split,
    splitMode,
    hasAssigneeMode,
    managerAssigneeIds,
    vacationWindow.from,
    vacationWindow.to,
  ]);
  // Load current user (needed for Include me)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isOpen) return;
      try {
        const m = await fetchMe();
        if (!cancelled) setMe(m || null);
      } catch {
        if (!cancelled) setMe(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, appRefreshKey]);

  function applyAssigneePrefillFromAssignment(a) {
    includeMeTouchedRef.current = false;
    setAssignmentCreatedByManagerId(a?.created_by_manager_id ?? null);

    const ass = Array.isArray(a?.assignees) ? a.assignees : [];
    const rawIds = Array.from(new Set(
      ass
        .filter(x => x && x.user_id != null)
        .map(x => String(x.user_id))
    ));

    const isManagerLikeRole = (r) => {
      const role = String(r || '').trim().toLowerCase();
      return role === 'manager' || role === 'admin';
    };

    const managerIds = rawIds.filter(uid => {
      const row = ass.find(x => x && String(x.user_id) === String(uid));
      if (isManagerLikeRole(row?.role)) return true;
      return managerIdSet.has(String(uid));
    });

    const employeeIds = rawIds.filter(uid => !managerIds.includes(String(uid)));

    setAssignees(employeeIds.map(uid => {
      const row = ass.find(x => x && String(x.user_id) === String(uid));
      return {
        user_id: uid,
        planned_hours_split: row?.planned_hours_split != null ? String(row.planned_hours_split) : undefined,
      };
    }));

    // If the assignment has manager assignees, show them in Managers section.
    setSelectedManagerIds(managerIds);

    setOriginalAssigneeIds(rawIds);
    setCreatedNewAssigneeIds([]);

    const isMeAssigned = !!(meId && rawIds.some(uid => String(uid) === String(meId)));
    const isMeOwner = !!(meId && a?.created_by_manager_id && String(a.created_by_manager_id) === String(meId));
    const plannedHours = Number(a?.planned_hours || 0);
    const defaultIncludeMe = isMeAssigned || (isMeOwner && (rawIds.length > 0 || plannedHours > 0));
    setIncludeMe(defaultIncludeMe);

    // Prefill manager bucket if present and there are no explicit assignees.
    const vm = a?.visibility_manager_user_id != null ? String(a.visibility_manager_user_id) : '';
    if (rawIds.length === 0 && vm) {
      setSelectedManagerIds([vm]);
    }
  }


  useEffect(() => {
    // UI-only invariant: if start_date is set, do not allow due_date before it.
    // This also repairs any invalid state coming from prefills/edits.
    if (!form.start_date) return;
    if (!form.due_date) return;
    if (String(form.due_date) >= String(form.start_date)) return;
    setForm(f => {
      if (!f.start_date || !f.due_date) return f;
      if (String(f.due_date) >= String(f.start_date)) return f;
      return { ...f, due_date: f.start_date };
    });
  }, [form.start_date, form.due_date]);

  const originalAssigneeIdSet = useMemo(
    () => new Set((Array.isArray(originalAssigneeIds) ? originalAssigneeIds : []).filter(v => v != null)),
    [originalAssigneeIds]
  );

  useEffect(() => {
    // Sync internal project selection with prop changes.
    // In edit mode, project is resolved from the assignment payload.
    if (isEditing) return;
    setSelectedProject(project || null);
    setSelectedClientId(project?.client_id != null ? String(project.client_id) : '');
  }, [project?.id, isEditing]);

  // Prefill from initialAssignment (calendar-driven edit flow)
  useEffect(() => {
    if (!isEditing) return;
    if (!initialAssignment) return;
    const a = initialAssignment;

    if (a?.project_id) {
      setSelectedProject({
        id: a.project_id,
        name: a.project_name || `Project #${a.project_id}`,
        client_name: a.client_name || null,
      });
    }

    setForm(f => ({
      ...f,
      job_id: a.job_id != null && a.job_id !== '' ? String(a.job_id) : (f.job_id || ''),
      task_id: a.task_id != null && a.task_id !== '' ? String(a.task_id) : (f.task_id || ''),
      planned_hours: a.planned_hours != null && a.planned_hours !== '' ? String(a.planned_hours) : (f.planned_hours || ''),
      start_date: a.start_date || f.start_date || '',
      due_date: a.due_date || f.due_date || '',
      notes: (a.notes != null ? String(a.notes) : f.notes) || '',
      top_priority: a.top_priority != null ? !!a.top_priority : !!f.top_priority,
    }));

    const sm = String(a.split_mode || 'per_person').toLowerCase();
    if (sm === 'split_total' || sm === 'per_person') setSplitMode(sm);
    setSplit(false);
    const dm = String(a.distribution_mode || 'spread').toLowerCase();
    setDistributionMode(dm === 'per_day' ? 'per_day' : 'spread');
    if (a.include_weekends != null) setIncludeWeekends(!!a.include_weekends);

    applyAssigneePrefillFromAssignment(a);
  }, [initialAssignment, isEditing]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEditing) return;
      if (!effectiveAssignmentId) return;
      // If caller supplied initialAssignment (calendar), trust it and skip the fetch.
      if (initialAssignment) return;
      setLoadingAssignment(true);
      setError('');
      try {
        const a = await fetchAssignment(effectiveAssignmentId);
        if (cancelled || !a) return;

        setSelectedProject({
          id: a.project_id,
          name: a.project_name || `Project #${a.project_id}`,
          client_name: a.client_name || null,
        });

        setForm(f => ({
          ...f,
          job_id: a.job_id != null ? String(a.job_id) : '',
          task_id: a.task_id != null ? String(a.task_id) : '',
          planned_hours: a.planned_hours != null ? String(a.planned_hours) : '',
          start_date: a.start_date || '',
          due_date: a.due_date || '',
          notes: a.notes || '',
          top_priority: !!a.top_priority,
        }));

        const sm = String(a.split_mode || 'per_person').toLowerCase();
        if (sm === 'split_total' || sm === 'per_person') {
          setSplitMode(sm);
        } else {
          setSplitMode('per_person');
        }

        const dm = String(a.distribution_mode || 'spread').toLowerCase();
        setDistributionMode(dm === 'per_day' ? 'per_day' : 'spread');

        setIncludeWeekends(!!a.include_weekends);

        applyAssigneePrefillFromAssignment(a);

        const ass = Array.isArray(a.assignees) ? a.assignees : [];
        const hasAnySplits = ass.some(x => x && x.planned_hours_split != null);
        setSplit(sm === 'split_total' && hasAnySplits);

        // Ensure task options are loaded for the selected job.
        if (a.job_id != null) {
          try {
            const t = await fetchJobTasks(a.job_id, { redirectOn401: false });
            if (!cancelled) setTasks(Array.isArray(t) ? t : []);
          } catch {
            if (!cancelled) setTasks([]);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load assignment');
      } finally {
        if (!cancelled) setLoadingAssignment(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveAssignmentId, initialAssignment, isEditing]);

  // If /me arrives after assignment prefill, set the default Include-me once.
  useEffect(() => {
    if (!isOpen) return;
    if (!isEditing) return;
    if (!meId) return;
    if (includeMeTouchedRef.current) return;

    const isMeAssigned = (Array.isArray(originalAssigneeIds) ? originalAssigneeIds : [])
      .some(uid => String(uid) === String(meId));
    const isMeOwner = assignmentCreatedByManagerId != null && String(assignmentCreatedByManagerId) === String(meId);
    const plannedHours = (() => {
      const n = Number(form.planned_hours || 0);
      return Number.isFinite(n) ? n : 0;
    })();
    const defaultIncludeMe = isMeAssigned || (isMeOwner && ((Array.isArray(originalAssigneeIds) ? originalAssigneeIds : []).length > 0 || plannedHours > 0));
    if (defaultIncludeMe) setIncludeMe(true);
  }, [assignmentCreatedByManagerId, form.planned_hours, isEditing, isOpen, meId, originalAssigneeIds]);

  useEffect(() => {
    // Only needed for the global entry point (no project passed in).
    let cancelled = false;
    (async () => {
      if (fixedProject) return;
      try {
        const list = await fetchProjectsList();
        if (!cancelled) setProjects(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, [fixedProject, appRefreshKey]);

  useEffect(() => {
    // Clients list (used to require Client selection before Project).
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchManagerClients();
        const rows = Array.isArray(res?.clients) ? res.clients : [];
        if (!cancelled) setClients(rows);
      } catch {
        if (!cancelled) setClients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [appRefreshKey]);

  // If opened from a project context without client_id, try to infer client selection from client_name.
  useEffect(() => {
    if (fixedProject) return;
    if (!project?.id) return;
    if (selectedClientId) return;
    const hint = String(project?.client_name || '').trim().toLowerCase();
    if (!hint) return;
    const match = (Array.isArray(clients) ? clients : []).find(c => String(c?.name || '').trim().toLowerCase() === hint);
    if (match?.id != null) setSelectedClientId(String(match.id));
  }, [clients, fixedProject, project?.id, project?.client_name, selectedClientId]);

  const filteredProjects = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    if (!selectedClientId) return [];
    return list.filter(p => String(p?.client_id ?? '') === String(selectedClientId));
  }, [projects, selectedClientId]);

  const plannedHoursNum = useMemo(() => {
    const raw = form.planned_hours;
    if (raw == null || raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, [form.planned_hours]);

  const assigneesOk = useMemo(() => {
    // Task-first rule: assignees are optional.
    // If you select at least one person, planned time becomes mandatory.
    const totalSelected = assignees.length + (hasAssigneeMode ? managerAssigneeIds.length : 0);
    return totalSelected === 0 || plannedHoursNum > 0;
  }, [assignees.length, hasAssigneeMode, managerAssigneeIds.length, plannedHoursNum]);

  useEffect(() => {
    if (!managerBucketError) return;
    // Clear stale message when the condition is fixed.
    const ids = Array.isArray(selectedManagerIds) ? selectedManagerIds : [];
    if (ids.length <= 1) setManagerBucketError('');
  }, [managerBucketError, selectedManagerIds]);

  const timeRequiredError = useMemo(() => {
    if (assigneesOk) return '';
    return 'Ako odabereš zaposlenika, moraš unijeti vrijeme.';
  }, [assigneesOk]);

  useEffect(() => {
    // Clear legacy/stale messages so the UI doesn't look “blocked” after changes.
    if (!error) return;
    if (error === 'Select at least one assignee') setError('');
    if (!timeRequiredError && error === 'Ako odabereš zaposlenika, moraš unijeti vrijeme.') setError('');
  }, [error, timeRequiredError]);

  const canSubmit = useMemo(() => {
    if (saving || loadingLookups) return false;
    if (!selectedProject?.id) return false;
    if (!form.due_date) return false;
    if (!form.job_id) return false;
    if (!assigneesOk) return false;
    if (splitMode === 'split_total' && split && assignees.length > 0) {
      if (hasAssigneeMode && managerAssigneeIds.length > 0) return false;
      const sum = assignees.reduce((acc, a) => acc + Number(a.planned_hours_split || 0), 0);
      if (Math.abs(sum - plannedHoursNum) > 0.01) return false;
    }
    const mgrIds = Array.isArray(selectedManagerIds) ? selectedManagerIds : [];
    if (mgrIds.length > 1) return false;
    return true;
  }, [assignees, assigneesOk, form.due_date, form.job_id, hasAssigneeMode, loadingLookups, managerAssigneeIds.length, plannedHoursNum, saving, selectedProject?.id, split, splitMode, selectedManagerIds]);

  const selectedAssigneeIds = useMemo(() => new Set(assignees.map(a => a.user_id)), [assignees]);

  const showVacationWarnings = useMemo(
    () => Array.isArray(vacationWarnings) && vacationWarnings.length > 0,
    [vacationWarnings]
  );

  const vacationWarningLines = useMemo(() => {
    if (!Array.isArray(vacationWarnings) || vacationWarnings.length === 0) return [];
    const idToName = new Map((Array.isArray(teamOptions) ? teamOptions : []).map(o => [String(o.id), String(o.name || '').trim()]));
    const lines = vacationWarnings.map((w) => {
      const uid = String(w?.personId || '');
      const name = idToName.get(uid) || `User #${uid}`;
      const type = String(w?.type || '').toUpperCase() === 'SICK' ? 'SICK' : 'VACATION';
      const date = String(w?.date || '').slice(0, 10);
      return {
        uid,
        name,
        type,
        date,
        label: `${name} — ${type} on ${formatDM(date)}`,
      };
    });

    lines.sort((a, b) => {
      const nc = String(a.name || '').localeCompare(String(b.name || ''));
      if (nc !== 0) return nc;
      const dc = String(a.date || '').localeCompare(String(b.date || ''));
      if (dc !== 0) return dc;
      return String(a.type || '').localeCompare(String(b.type || ''));
    });

    return lines;
  }, [teamOptions, vacationWarnings]);
  const allAssigneesSelected = useMemo(() => {
    if (!teamOptions.length) return false;
    return teamOptions.every(o => selectedAssigneeIds.has(o.id));
  }, [teamOptions, selectedAssigneeIds]);

  const someAssigneesSelected = useMemo(() => {
    if (!teamOptions.length) return false;
    return teamOptions.some(o => selectedAssigneeIds.has(o.id));
  }, [teamOptions, selectedAssigneeIds]);

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someAssigneesSelected && !allAssigneesSelected;
  }, [someAssigneesSelected, allAssigneesSelected]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const projectId = selectedProject?.id;
        setLoadingLookups(true);
        let allJobs = [];
        let managerIdSet = new Set();
        if (projectId) {
          const linked = await fetchProjectJobs(projectId, { redirectOn401: false });
          allJobs = Array.isArray(linked) ? linked : [];
        }

        try {
          const lk = await fetchPlanningLookups({ redirectOn401: false });
          const fromLookups = Array.isArray(lk?.jobs) ? lk.jobs.map(j => ({ id: j.id, name: j.name })) : [];
          if (fromLookups.length > 0) allJobs = fromLookups;

          const rawManagers = Array.isArray(lk?.managers) ? lk.managers : [];
          const norm = (s) => String(s || '').trim().toLowerCase();
          const orderKey = (m) => {
            const email = norm(m?.email);
            const name = norm(m?.display_name);
            const v = `${email} ${name}`;
            if (v.includes('vinko')) return 0;
            if (v.includes('sanela')) return 1;
            if (v.includes('andrea')) return 2;
            return 99;
          };
          const managersOut = rawManagers
            .filter(m => m && m.id != null)
            .map(m => ({ id: String(m.id), name: String(m.display_name || '').trim() || '—', email: m.email || null }))
            .filter(m => !isHiddenAssigneeName(m.name))
            .sort((a, b) => {
              const ka = orderKey(a);
              const kb = orderKey(b);
              if (ka !== kb) return ka - kb;
              return String(a.name || '').localeCompare(String(b.name || ''));
            });
          managerIdSet = new Set(managersOut.map(m => String(m.id)));
          if (!cancelled) setManagerOptions(managersOut);
        } catch (_) {}

        if (!cancelled) setJobs(allJobs);

        const gl = await fetchTeamLoad(rangeKey, null, { redirectOn401: false });
        const all = new Map();
        (overview?.team || []).forEach(t => all.set(t.user_id, { id: t.user_id, name: t.display_name }));
        (gl?.users || []).forEach(u => all.set(u.user_id, { id: u.user_id, name: u.display_name }));
        const people = Array.from(all.values())
          .filter(o => o && o.id != null)
          .filter(o => !managerIdSet.has(String(o.id)))
          .filter(o => !isHiddenAssigneeName(o.name))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!cancelled) setTeamOptions(people);
      } catch (err) {
        console.error('SharedAddWorkModal init error', err);
        if (!cancelled) setError(err?.message || 'Failed to load modal data');
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject?.id, rangeKey, overview]);

  function toggleManagerBucket(uid) {
    const id = String(uid);
    setSelectedManagerIds(prev => {
      const cur = Array.isArray(prev) ? prev : [];
      const has = cur.includes(id);
      if (has) return cur.filter(x => x !== id);
      if (cur.length >= 1) {
        // If multiple managers are selected, keep UX simple and force single-select.
        setManagerBucketError('Select exactly one manager.');
        return [id];
      }
      return [id];
    });
  }

  function toggleIncludeMe(nextChecked) {
    const checked = !!nextChecked;
    includeMeTouchedRef.current = true;
    setIncludeMe(checked);
    if (!meId) return;
    if (!isMeManagerLike) {
      setAssignees(prev => {
        const cur = Array.isArray(prev) ? prev : [];
        const has = cur.some(a => String(a?.user_id) === String(meId));
        if (checked && !has) return [...cur, { user_id: String(meId) }];
        if (!checked && has) return cur.filter(a => String(a?.user_id) !== String(meId));
        return cur;
      });
    }
  }

  useEffect(() => {
    // Prefill assignees once team options are available.
    if (isEditing) return;
    if (!initialAssigneeIds || initialAssigneeIds.length === 0) return;
    if (!teamOptions.length) return;
    setAssignees(prev => {
      if (prev && prev.length) return prev;
      const allowed = new Set(teamOptions.map(t => t.id));
      return initialAssigneeIds
        .filter(id => allowed.has(id))
        .map(id => ({ user_id: id }));
    });
  }, [teamOptions, initialAssigneeIds, isEditing]);

  async function onSelectJob(jobId) {
    setForm(f => ({ ...f, job_id: jobId || '', task_id: '' }));
    if (!jobId) {
      setTasks([]);
      return;
    }
    try {
      const t = await fetchJobTasks(jobId, { redirectOn401: false });
      setTasks(Array.isArray(t) ? t : []);
    } catch (_) {
      setTasks([]);
    }
  }

  function toggleAssignee(uid) {
    setAssignees(prev => prev.some(a => a.user_id === uid)
      ? prev.filter(a => a.user_id !== uid)
      : [...prev, { user_id: uid }]);
  }

  function toggleAllAssignees() {
    setAssignees(prev => {
      if (allAssigneesSelected) {
        return [];
      }
      const prevById = new Map(prev.map(a => [a.user_id, a]));
      return teamOptions
        .filter(o => o && o.id != null)
        .map(o => prevById.get(o.id) || { user_id: o.id });
    });
  }

  function setSplitHours(uid, val) {
    setAssignees(prev => prev.map(a => a.user_id === uid ? { ...a, planned_hours_split: val } : a));
  }

  function setHoursMode(nextMode) {
    const mode = String(nextMode || '').toLowerCase();
    if (!['per_person', 'split_total'].includes(mode)) return;
    setSplitMode(mode);
    if (mode === 'per_person') {
      setSplit(false);
      setAssignees(prev => prev.map(a => ({ user_id: a.user_id })));
    } else {
      // Per-day only applies to per_person mode
      setDistributionMode('spread');
    }
  }

  function setDistMode(nextMode) {
    const mode = String(nextMode || '').toLowerCase();
    if (!['spread', 'per_day'].includes(mode)) return;
    if (splitMode !== 'per_person') {
      setDistributionMode('spread');
      return;
    }
    setDistributionMode(mode);
  }

  async function submit() {
    setError('');
    setManagerBucketError('');
    if (!selectedProject?.id) { setError('Project is required'); return; }
    const ph = plannedHoursNum;
    if (!form.due_date) { setError('Due date is required'); return; }
    if (!form.job_id) { setError('Job is required'); return; }

    // For planned work, require a more specific description.
    // In this UI, the Task selection is the "description" of the planned work.
    if (!isEditing && ph > 0 && !form.task_id) { setError('Task is required when planned hours is set'); return; }

    const notesTrimmed = String(form.notes || '').trim();

    if (repeatWeekly && isEditing) {
      setError('Repeat weekly is only supported when creating a new task.');
      return;
    }
    if (repeatWeekly) {
      if (!form.start_date) { setError('Start date is required when repeat weekly is enabled'); return; }
      if (!repeatUntil) { setError('Repeat until is required when repeat weekly is enabled'); return; }
      if (String(repeatUntil) < String(form.start_date)) { setError('Repeat until must be on or after start date'); return; }
    }

    const totalSelected = assignees.length + (hasAssigneeMode ? managerAssigneeIds.length : 0);
    if (totalSelected > 0 && !(ph > 0)) { setError('Ako odabereš zaposlenika, moraš unijeti vrijeme.'); return; }

    const mgrIds = Array.isArray(selectedManagerIds) ? selectedManagerIds : [];
    if (mgrIds.length > 1) { setManagerBucketError('Select exactly one manager.'); return; }
    if (splitMode === 'split_total' && split) {
      const sum = assignees.reduce((acc, a) => acc + Number(a.planned_hours_split || 0), 0);
      if (Math.abs(sum - ph) > 0.01) { setError('Split hours must sum to planned hours'); return; }
    }

    setSaving(true);
    try {
      const includeSplits = splitMode === 'split_total' && split;
      if (isEditing) {
        const employeeIds = Array.from(new Set(
          (Array.isArray(assignees) ? assignees : [])
            .map(a => a?.user_id)
            .filter(v => v != null)
            .map(String)
        ));

        const assigneeIds = hasAssigneeMode
          ? Array.from(new Set([...employeeIds, ...(Array.isArray(managerAssigneeIds) ? managerAssigneeIds : [])]))
          : [];

        const splitById = new Map(
          (Array.isArray(assignees) ? assignees : [])
            .filter(a => a && a.user_id != null)
            .map(a => [String(a.user_id), Number(a.planned_hours_split || 0)])
        );

        const includeSplits = splitMode === 'split_total' && split;
        if (includeSplits && hasAssigneeMode && managerAssigneeIds.length > 0) {
          setError('Custom split is not supported when Managers are selected.');
          return;
        }

        const updatePayload = {
          job_id: Number(form.job_id),
          task_id: form.task_id ? Number(form.task_id) : null,
          planned_hours: ph,
          split_mode: splitMode,
          distribution_mode: splitMode === 'per_person' ? distributionMode : 'spread',
          include_weekends: !!includeWeekends,
          start_date: form.start_date || null,
          due_date: form.due_date,
          notes: notesTrimmed || null,
          top_priority: !!form.top_priority,

          assignee_user_ids: assigneeIds,
          assignee_splits: includeSplits
            ? Object.fromEntries(assigneeIds.map(uid => [uid, Number(splitById.get(uid) || 0)]))
            : null,

          // Manager bucket only applies when there are no assignees.
          visibility_manager_user_id: (assigneeIds.length > 0) ? null : (selectedManagerId || null),
        };

        await updateAssignment(effectiveAssignmentId, updatePayload);

        toast.success('Work updated');
        bumpRefresh && bumpRefresh();
        onClose && onClose();
        onSaved && onSaved();
      } else {
        const includeSplits = splitMode === 'split_total' && split;
        if (includeSplits && hasAssigneeMode && managerAssigneeIds.length > 0) {
          setError('Custom split is not supported when Managers are selected.');
          return;
        }

        const employeeIds = (Array.isArray(assignees) ? assignees : [])
          .map(a => a?.user_id)
          .filter(v => v != null)
          .map(String);
        const allAssigneeIds = hasAssigneeMode
          ? Array.from(new Set([...employeeIds, ...(Array.isArray(managerAssigneeIds) ? managerAssigneeIds : [])]))
          : [];

        const payload = {
          job_id: Number(form.job_id),
          task_id: form.task_id ? Number(form.task_id) : undefined,
          planned_hours: ph,
          split_mode: splitMode,
          distribution_mode: splitMode === 'per_person' ? distributionMode : 'spread',
          include_weekends: !!includeWeekends,
          start_date: repeatWeekly ? String(form.start_date) : (form.start_date || undefined),
          due_date: repeatWeekly ? String(form.start_date) : form.due_date,
          visibility_manager_user_id: allAssigneeIds.length > 0 ? null : (selectedManagerId || null),
          assignees: includeSplits
            ? allAssigneeIds.map(uid => ({ user_id: uid, planned_hours_split: Number((assignees.find(a => String(a.user_id) === String(uid))?.planned_hours_split) || 0) }))
            : allAssigneeIds.map(uid => ({ user_id: uid })),
          notes: notesTrimmed || null,
          top_priority: !!form.top_priority,
          repeat_weekly: repeatWeekly ? true : undefined,
          repeat_until: repeatWeekly ? String(repeatUntil) : undefined,
        };

        const created = await createProjectAssignment(selectedProject.id, payload, { redirectOn401: false });
        const createdCount = Number(created?.created_count || 0);
        if (createdCount > 1) {
          toast.success(`Created ${createdCount} recurring tasks`);
        } else {
          toast.success('Work planned');
        }
        bumpRefresh && bumpRefresh();
        onClose && onClose();
        onCreated && onCreated();
      }
    } catch (err) {
      console.error('SharedAddWorkModal submit error', err);
      const msg = err?.message || (isEditing ? 'Failed to update assignment' : 'Failed to create assignment');
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[6px]" onClick={() => onClose && onClose()} />
      <div className="absolute right-1/2 translate-x-1/2 top-16 w-[720px] max-w-[96vw] bg-white dark:bg-gray-900 rounded shadow-lg p-4 max-h-[80vh] overflow-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="text-lg font-semibold">{isEditing ? 'Edit work' : 'Add work'} — {selectedProject?.name || (selectedProject?.id ? `Project #${selectedProject.id}` : 'Select project')}</div>
          <button onClick={() => onClose && onClose()} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
        </div>

        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}
        {loadingLookups && <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Loading options…</div>}
        {loadingAssignment && <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Loading assignment…</div>}

        {!fixedProject && (
          <div className="grid grid-cols-2 gap-3 text-sm mb-3">
            <label className="flex flex-col col-span-2">
              <span className="mb-1">Client</span>
              <select
                value={selectedClientId}
                onChange={(e) => {
                  const nextClientId = String(e.target.value || '');
                  setSelectedClientId(nextClientId);
                  setSelectedProject(null);
                  setForm(f => ({ ...f, job_id: '', task_id: '' }));
                  setTasks([]);
                  setError('');
                }}
                className="border rounded px-2 py-1 bg-white dark:bg-gray-800"
                disabled={loadingLookups}
              >
                <option value="">Select client</option>
                {(Array.isArray(clients) ? clients : []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>

            <label className="flex flex-col col-span-2">
              <span className="mb-1">Project</span>
              <select
                value={selectedProject?.id ? String(selectedProject.id) : ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  const next = filteredProjects.find(p => Number(p.id) === Number(id)) || null;
                  setSelectedProject(next);
                  setForm(f => ({ ...f, job_id: '', task_id: '' }));
                  setTasks([]);
                  setError('');
                }}
                className="border rounded px-2 py-1 bg-white dark:bg-gray-800"
                disabled={loadingLookups || !selectedClientId}
              >
                <option value="">Select project</option>
                {filteredProjects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="flex flex-col">
            <span className="mb-1">Job</span>
            <select value={form.job_id} onChange={e => onSelectJob(e.target.value)} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" disabled={loadingLookups}>
              <option value="">Select job</option>
              {jobs.map(j => (<option key={j.id} value={j.id}>{j.name}</option>))}
            </select>
          </label>

          <label className="flex flex-col">
            <span className="mb-1">Task (required when planned hours &gt; 0)</span>
            <select value={form.task_id} onChange={e => setForm(f => ({ ...f, task_id: e.target.value }))} disabled={!form.job_id} className="border rounded px-2 py-1 bg-white dark:bg-gray-800">
              <option value="">No task</option>
              {tasks.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
            </select>
          </label>

          <label className="flex flex-col">
            <span className="mb-1">Planned hours {splitMode === 'per_person' ? '(per person)' : '(total)'}</span>
            <input type="number" min="0" step="0.25" value={form.planned_hours} onChange={e => setForm(f => ({ ...f, planned_hours: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
          </label>
          <div className="flex flex-col">
            <span className="mb-1">Hours mode</span>
            <div className="flex items-center gap-3 text-xs">
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="hours_mode" checked={splitMode === 'per_person'} onChange={() => setHoursMode('per_person')} />
                <span>Per person</span>
              </label>
              <label className="inline-flex items-center gap-2">
                <input type="radio" name="hours_mode" checked={splitMode === 'split_total'} onChange={() => setHoursMode('split_total')} />
                <span>Split total</span>
              </label>
            </div>
            <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">
              {splitMode === 'per_person'
                ? 'Each selected person gets the full planned hours.'
                : 'Planned hours is the total; it will be divided across assignees (or you can set custom amounts).'
              }
            </div>

            <div className="mt-2">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Distribution</div>
              <div className="flex items-center gap-3 text-xs mt-1">
                <label className={`inline-flex items-center gap-2 ${splitMode !== 'per_person' ? 'opacity-50' : ''}`}>
                  <input
                    type="radio"
                    name="distribution_mode"
                    checked={distributionMode === 'per_day'}
                    onChange={() => setDistMode('per_day')}
                    disabled={splitMode !== 'per_person'}
                  />
                  <span>Per Day</span>
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="distribution_mode"
                    checked={distributionMode === 'spread'}
                    onChange={() => setDistMode('spread')}
                  />
                  <span>Spread</span>
                </label>
              </div>
              <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">
                {distributionMode === 'per_day'
                  ? 'Planned hours is per day (total scales with number of eligible days).'
                  : 'Planned hours is total (spread over eligible days).'
                }
              </div>
            </div>

            <label className="flex items-center gap-2 mt-2 text-xs">
              <input type="checkbox" checked={!!includeWeekends} onChange={(e) => setIncludeWeekends(e.target.checked)} />
              <span>Include weekends</span>
            </label>
          </div>

          <div className="col-span-2 grid grid-cols-2 gap-3">
            <label className="flex flex-col">
              <span className="mb-1">Start date (optional)</span>
              <DatePicker
                value={form.start_date}
                onChange={(ymd) => setForm(f => {
                  const next = { ...f, start_date: ymd };
                  if (repeatWeekly) {
                    if (ymd) next.due_date = ymd;
                  } else if (ymd && f.due_date && String(f.due_date) < String(ymd)) {
                    next.due_date = ymd;
                  }
                  return next;
                })}
                className="w-full"
              />
            </label>

            <label className="flex flex-col">
              <span className="mb-1">Due date</span>
              <DatePicker
                value={form.due_date}
                min={form.start_date || ''}
                onChange={(ymd) => setForm(f => repeatWeekly
                  ? ({ ...f, start_date: ymd, due_date: ymd })
                  : ({ ...f, due_date: ymd })
                )}
                className="w-full"
              />
            </label>
          </div>

          <div className="col-span-2">
            <label className="flex items-center gap-2 mt-1 text-xs select-none">
              <input
                type="checkbox"
                checked={!!repeatWeekly}
                onChange={(e) => {
                  const next = !!e.target.checked;
                  setRepeatWeekly(next);
                  if (!next) {
                    setRepeatUntil('');
                    setRepeatSingleDayNote(false);
                    return;
                  }

                  const needsSingleDay = !!(form.start_date && form.due_date && String(form.start_date) !== String(form.due_date));
                  setRepeatSingleDayNote(needsSingleDay);
                  if (needsSingleDay) {
                    setForm(f => ({ ...f, due_date: f.start_date }));
                  }
                }}
              />
              <span>Repeat weekly</span>
            </label>

            {repeatWeekly && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                <label className="flex flex-col">
                  <span className="mb-1">Repeat until</span>
                  <DatePicker
                    value={repeatUntil}
                    min={form.start_date || ''}
                    onChange={(ymd) => setRepeatUntil(ymd)}
                    className="w-full"
                  />
                </label>

                <div className="flex flex-col justify-end">
                  <div className="text-[11px] text-gray-600 dark:text-gray-400">
                    Creates a task every {repeatWeekday || '—'} until {repeatUntil ? String(repeatUntil).slice(0, 10) : '…'}
                  </div>
                  {repeatSingleDayNote && (
                    <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">
                      Repeat weekly creates single-day tasks, so due date was set to start date.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="col-span-2">
            <div className="mb-1">Managers</div>
            {meId && isMeManagerLike && (
              <label className="flex items-center gap-2 text-xs mb-2 select-none">
                <input type="checkbox" checked={!!includeMe} onChange={(e) => toggleIncludeMe(e.target.checked)} />
                <span>Include me</span>
              </label>
            )}
            <div className="grid gap-2 border rounded p-2 dark:border-gray-700 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
              {managerOptions.map(opt => (
                <label key={opt.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none text-sm">
                  <input
                    type="checkbox"
                    className="h-5 w-5"
                    checked={(Array.isArray(selectedManagerIds) ? selectedManagerIds : []).includes(opt.id)}
                    onChange={() => toggleManagerBucket(opt.id)}
                  />
                  <span className="flex-1 min-w-0 whitespace-normal break-normal leading-snug">{opt.name}</span>
                </label>
              ))}
              {managerOptions.length === 0 && (
                <div className="text-[11px] text-gray-600 dark:text-gray-400 px-2 py-1">No managers available</div>
              )}
            </div>

            {managerBucketError && (
              <div className="mt-1 text-[11px] text-red-600">{managerBucketError}</div>
            )}
            {splitMode === 'split_total' && split && hasAssigneeMode && managerAssigneeIds.length > 0 && (
              <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">Custom split is not supported when Managers are selected.</div>
            )}
          </div>

          <div className="col-span-2">
            <div className="mb-1">Employees</div>
            <div className="grid gap-2 border rounded p-2 dark:border-gray-700 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
              {teamOptions.length > 0 && (
                <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none text-sm">
                  <input ref={selectAllRef} type="checkbox" className="h-5 w-5" checked={allAssigneesSelected} onChange={toggleAllAssignees} />
                  <span className="font-semibold">All</span>
                </label>
              )}
              {teamOptions.map(opt => (
                <label key={opt.id} className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer select-none text-sm">
                  <input type="checkbox" className="h-5 w-5" checked={assignees.some(a => a.user_id === opt.id)} onChange={() => toggleAssignee(opt.id)} />
                  <span className="flex-1 min-w-0 whitespace-normal break-normal leading-snug">{opt.name}</span>
                  {splitMode === 'split_total' && split && assignees.some(a => a.user_id === opt.id) && (
                    <input
                      type="number"
                      min="0"
                      step="0.25"
                      className="w-20 shrink-0 border rounded px-1 py-0.5 ml-2"
                      placeholder="hrs"
                      value={(assignees.find(a => a.user_id === opt.id)?.planned_hours_split) || ''}
                      onChange={e => setSplitHours(opt.id, e.target.value)}
                    />
                  )}
                </label>
              ))}
            </div>

            {assignees.length === 0 && (
              <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-400">Unassigned</div>
            )}
            {timeRequiredError && (
              <div className="mt-1 text-[11px] text-red-600">{timeRequiredError}</div>
            )}

            {splitMode === 'split_total' && (
              <label className="flex items-center gap-2 mt-2 text-xs">
                <input type="checkbox" checked={split} onChange={e => setSplit(e.target.checked)} />
                <span>Set custom hours per person</span>
              </label>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-5 w-5"
              checked={!!form.top_priority}
              onChange={(e) => setForm(f => ({ ...f, top_priority: e.target.checked }))}
            />
            <span>! Priority</span>
          </label>

          <label className="flex flex-col col-span-2">
            <span className="mb-1">Notes</span>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="border rounded px-2 py-1 bg-white dark:bg-gray-800" />
          </label>

          {showVacationWarnings && (
            <div className="col-span-2 mt-2 border border-amber-200 dark:border-amber-900/50 rounded p-3 bg-amber-50/60 dark:bg-amber-900/10">
              <div className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                Vacation/Sick overlap warning
              </div>
              <div className="text-[11px] text-amber-800 dark:text-amber-200 mt-1">
                Planned window: {formatDM(vacationWindow.from)}–{formatDM(vacationWindow.to)}
              </div>
              <div className="mt-2 space-y-1">
                {vacationWarningLines.map((w, idx) => (
                  <div key={`${w.uid}-${w.date}-${w.type}-${idx}`} className="text-[12px] text-amber-900 dark:text-amber-100">
                    {w.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="col-span-2 flex items-center justify-end gap-2 mt-2">
            <button onClick={() => onClose && onClose()} className="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700">Cancel</button>
            <button onClick={submit} disabled={!canSubmit} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : (isEditing ? 'Save changes' : 'Save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
